import { ChangeDetectionStrategy, Component, DestroyRef, inject, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, switchMap, take } from 'rxjs/operators';

import { CurrentUserService, UsersService } from './core/services';
import { ROUTER_LINKS } from './interfaces/router-links.enum';
import { NavBarComponent } from './nav-bar/nav-bar.component';
import { ConfirmService } from './shared/confirm';
import { IconsComponent } from './shared/icons/icons.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconsComponent, NavBarComponent, RouterOutlet],
  providers: [ConfirmService]
})
export class AppComponent implements OnInit {
  readonly #dr = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #currentUser = inject(CurrentUserService);
  readonly #confirm = inject(ConfirmService);
  readonly #usersService = inject(UsersService);

  protected readonly showLoader = signal(true);
  public isLoggedIn = false;
  protected readonly showNavbar = signal(false);

  public ngOnInit(): void {
    this.isLoggedIn = !!this.#currentUser.profile;
    this.showNavbar.set(this.#isNavbarRoute(location.href));

    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#dr)
      )
      .subscribe(event => {
        this.showNavbar.set(this.#isNavbarRoute((event as NavigationEnd).urlAfterRedirects));
      });

    this.#checkTransfer();
  }

  public onActivate(): void {
    this.showLoader.set(false);
  }

  #isNavbarRoute(route: string): boolean {
    return [
      ROUTER_LINKS.HOME,
      ROUTER_LINKS.STAFF,
      ROUTER_LINKS.TEAM,
      ROUTER_LINKS.REGISTRY_REVIEW,
      ROUTER_LINKS.ANALYTICS,
      ROUTER_LINKS.SETTINGS,
      ROUTER_LINKS.PREVIEW,
      ROUTER_LINKS.KNOWLEDGE_BASE
    ].some(link => route.includes(link));
  }

  #checkTransfer(): void {
    this.#router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd &&
            !(event as NavigationEnd).urlAfterRedirects.includes(ROUTER_LINKS.ONBOARDING)
        ),
        take(1),
        switchMap(() => this.#currentUser.getLiveProfile$()),
        filter(user => Boolean(user && user.firstLogin && user.transferManagerId)),
        exhaustMap(user =>
          this.#confirm
            .open({
              buttons: [
                {
                  class: 'btn btn-lg btn-active m-auto',
                  label: 'joinTeam',
                  value: 'join'
                },
                {
                  class: 'btn btn-lg btn-info m-auto',
                  label: 'declineInvitation',
                  value: 'decline'
                }
              ],
              coloredHeader: true,
              icon: 'group',
              label: 'inviteTeamTitle',
              message: 'inviteTeamMessage',
              panelClass: 'team-invitation',
              params: {
                manager: user!.transferManagerFullName
              },
              backdrop: 'static',
              keyboard: false,
              hideClose: true
            })
            .pipe(
              switchMap((answer: string) => {
                switch (answer) {
                  case 'join':
                    return this.#usersService.joinTeam(user!);
                  case 'decline':
                    return this.#usersService.declineTeam(user!);
                  default:
                    return of(false);
                }
              }),
              catchError(err => {
                console.error(err);
                return of(void 0);
              })
            )
        ),
        takeUntilDestroyed(this.#dr)
      )
      .subscribe();
  }
}
